/**
 * AccountSheet — create an account, sign in, confirm an email, or look at your own profile.
 * (Since 27 Sep the profile is a page of its own, /you, src/components/ProfilePage.tsx; the feed's
 * account button goes there. The profile view here stays for the fixture and as the sheet's
 * twin of that page.)
 *
 * The account is what makes linking possible: an anonymous visitor can play everything, but
 * only an account with a handle can put something in the feed. Creating one upgrades the
 * anonymous session in place (see src/api/account.ts), so nothing the person already did is
 * lost. Every refusal comes back as a sentence, and every view names its next step.
 */
import { For, Match, Show, Switch, createEffect, createResource, createSignal, on, onCleanup } from 'solid-js';
import { MakerPanel } from './MakerPanel';
import { account as api, validateEmail, validateHandle, validatePassword, type Account } from '../api/account';
import { fetchStats, type DailyRow, type Totals } from '../api/stats';

export type AccountView = 'create' | 'signin' | 'confirm' | 'profile';

/** The numbers, for an admin: the last 14 days and the totals. DAU = profiles that opened Blitz that day. */
function StatsPanel() {
  const [stats] = createResource(() => fetchStats(14));
  return (
    <div class="stats-panel" data-testid="stats">
      <h4 style="font:600 11px/1 var(--mono);letter-spacing:.14em;text-transform:uppercase;color:var(--fg-faint);margin:14px 0 8px">The numbers · last 14 days · UTC</h4>
      {/* The page has all of it — retention, the baseline, card_rank — and opening it starts no card session. */}
      <p class="hint">
        <a class="linkish" href="/stats" data-testid="stats-link">All the numbers, on a page: /stats</a>
      </p>
      <Show when={stats()} fallback={<p class="hint">Loading…</p>}>
        {(s) => (
          <Show when={!('error' in s())} fallback={<p class="error">{(s() as { error: string }).error}</p>}>
            {(() => {
              const d = s() as { daily: DailyRow[]; totals: Totals | null };
              return (
                <>
                  <Show when={d.totals}>
                    {(t) => (
                      <p class="totals">
                        <span><b>{t().actors}</b> profiles</span>
                        <span><b>{t().accounts}</b> accounts</span>
                        <span><b>{t().handles}</b> handles</span>
                        <span><b>{t().live_cards}</b> live cards</span>
                        <span><b>{t().linked_cards}</b> by people</span>
                        <span><b>{t().comments}</b> comments</span>
                        <span><b>{t().sessions_all_time}</b> sessions ever</span>
                      </p>
                    )}
                  </Show>
                  <div class="stats-wrap">
                    <table class="stats">
                      <thead>
                        <tr>
                          <th>day</th>
                          <th title="distinct profiles with a card session">DAU</th>
                          <th title="first session ever">new</th>
                          <th title="DAU behind a real account">acct</th>
                          <th>sessions</th>
                          <th>cards</th>
                          <th>comments</th>
                          <th>added</th>
                        </tr>
                      </thead>
                      <tbody>
                        <For each={[...d.daily].reverse()}>
                          {(r) => (
                            <tr>
                              <td>{r.day.slice(5)}</td>
                              <td>{r.dau}</td>
                              <td>{r.new_actors}</td>
                              <td>{r.account_dau}</td>
                              <td>{r.sessions}</td>
                              <td>{r.cards_played}</td>
                              <td>{r.comments}</td>
                              <td>{r.cards_added}</td>
                            </tr>
                          )}
                        </For>
                      </tbody>
                    </table>
                  </div>
                </>
              );
            })()}
          </Show>
        )}
      </Show>
    </div>
  );
}

export interface AccountSheetProps {
  open: boolean;
  view: AccountView;
  account: Account;
  onView: (v: AccountView) => void;
  onClose: () => void;
  /** The account changed (created, signed in, signed out, handle set). */
  onAccount: (a: Account) => void;
  toast: (message: string) => void;
  /** Open a card from the profile's lists: in this feed if it is here, else by its link. */
  onGoTo: (id: string) => void;
  /** A card of this person's was taken down. */
  onRemoved: (id: string) => void;
}

export function AccountSheet(props: AccountSheetProps) {
  const [email, setEmail] = createSignal('');
  const [password, setPassword] = createSignal('');
  const [handle, setHandle] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [changingHandle, setChangingHandle] = createSignal(false);
  let first: HTMLInputElement | undefined;

  // A new view starts clean and focused: an error from the create form has no field on the
  // sign-in form, and Tab must not be left on <body> behind the veil.
  createEffect(
    on(
      () => [props.open, props.view] as const,
      () => {
        if (!props.open) return;
        setError(null);
        queueMicrotask(() => first?.focus());
      },
    ),
  );

  createEffect(() => {
    if (!props.open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') props.onClose();
    };
    window.addEventListener('keydown', onKey);
    onCleanup(() => window.removeEventListener('keydown', onKey));
  });

  // Client-side checks run first so the common slips never cost a round-trip.
  const precheck = (fields: Array<{ error: string } | Record<string, string>>): boolean => {
    for (const f of fields) {
      if ('error' in f) {
        setError(f.error);
        return false;
      }
    }
    return true;
  };

  const run = async (fn: () => Promise<void>) => {
    if (busy()) return;
    setBusy(true);
    setError(null);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  const create = (e: Event) => {
    e.preventDefault();
    if (!precheck([validateEmail(email()), validatePassword(password()), validateHandle(handle())])) return;
    void run(async () => {
      const r = await api.create({ email: email(), password: password(), handle: handle() });
      if (!r.ok) {
        setError(r.reason);
        return;
      }
      props.onAccount(r.account);
      setPassword('');
      if (r.needsConfirmation) {
        props.onView('confirm');
      } else if (r.handleProblem) {
        // The account is real; only the name was refused. Say so, and stay on the picker.
        props.toast('Account created. Pick a different handle.');
        props.onView('profile');
        queueMicrotask(() => setError(r.handleProblem ?? null));
      } else {
        props.toast(`Welcome, @${r.account.handle ?? handle()}. Link anything.`);
        props.onClose();
      }
    });
  };

  const signIn = (e: Event) => {
    e.preventDefault();
    if (!precheck([validateEmail(email())])) return;
    void run(async () => {
      const r = await api.signIn({ email: email(), password: password() });
      if (!r.ok) {
        setError(r.reason);
        return;
      }
      props.onAccount(r.account);
      setPassword('');
      if (r.needsConfirmation) {
        props.onView('confirm');
      } else {
        props.toast(r.account.handle ? `Signed in as @${r.account.handle}` : 'Signed in');
        props.onClose();
      }
    });
  };

  const confirmed = () =>
    void run(async () => {
      const a = await api.refresh(); // a fresh token, so a confirmation done elsewhere shows up now
      props.onAccount(a);
      if (a.isAccount) {
        props.toast(a.handle ? `Confirmed. You are @${a.handle}.` : 'Confirmed.');
        props.onView('profile');
      } else {
        setError('Not confirmed yet — the link is in your inbox. It can take a minute to arrive.');
      }
    });

  const resend = () =>
    void run(async () => {
      const r = await api.resend();
      if (!r.ok) {
        setError(r.reason);
        return;
      }
      props.toast('Sent again. Check spam too.');
    });

  const saveHandle = (e: Event) => {
    e.preventDefault();
    if (!precheck([validateHandle(handle())])) return;
    void run(async () => {
      const r = await api.claim(handle());
      if (!r.ok) {
        setError(r.reason);
        return;
      }
      props.onAccount({ ...props.account, handle: r.handle });
      props.toast(`You are @${r.handle}.`);
      setHandle('');
      setChangingHandle(false);
    });
  };

  const signOut = () =>
    void run(async () => {
      const a = await api.signOut();
      props.onAccount(a);
      props.toast('Signed out');
      props.onClose();
    });

  return (
    <Show when={props.open}>
      <div class="sheet-veil" onClick={() => props.onClose()} aria-hidden="true" />
      <div class="link-sheet account-sheet" data-ui data-testid="account-sheet" data-view={props.view} role="dialog" aria-modal="true" aria-label="Account">
        <Switch>
          <Match when={props.view === 'create'}>
            {/* novalidate: the browser's bubble would pre-empt our own sentences. */}
            <form onSubmit={create} novalidate>
              <h2>Create your account</h2>
              <p class="hint">Everything you have seen stays with you. An account lets you add what you made to the feed under your handle.</p>
              <label>
                <span>Email</span>
                <input ref={first} type="email" inputmode="email" autocomplete="username" value={email()} onInput={(e) => setEmail(e.currentTarget.value)} data-testid="acct-email" />
              </label>
              <label>
                <span>Password</span>
                <input type="password" autocomplete="new-password" value={password()} onInput={(e) => setPassword(e.currentTarget.value)} data-testid="acct-password" />
              </label>
              <label>
                <span>Handle</span>
                <div class="at">
                  <b>@</b>
                  <input type="text" autocomplete="nickname" autocapitalize="off" spellcheck={false} placeholder="yourname" value={handle()} onInput={(e) => setHandle(e.currentTarget.value)} data-testid="acct-handle" />
                </div>
              </label>
              <Show when={error()}>
                <p class="error" role="alert" data-testid="acct-error">{error()}</p>
              </Show>
              <div class="row">
                <button type="button" class="btn" onClick={() => props.onView('signin')} data-testid="acct-to-signin">
                  I have an account
                </button>
                <button type="submit" class="btn primary" disabled={busy()} data-testid="acct-submit">
                  {busy() ? 'Creating…' : 'Create account'}
                </button>
              </div>
            </form>
          </Match>

          <Match when={props.view === 'signin'}>
            <form onSubmit={signIn} novalidate>
              <h2>Sign in</h2>
              <p class="hint">Your handle, your links, on this device too.</p>
              <label>
                <span>Email</span>
                <input ref={first} type="email" inputmode="email" autocomplete="username" value={email()} onInput={(e) => setEmail(e.currentTarget.value)} data-testid="acct-email" />
              </label>
              <label>
                <span>Password</span>
                <input type="password" autocomplete="current-password" value={password()} onInput={(e) => setPassword(e.currentTarget.value)} data-testid="acct-password" />
              </label>
              <Show when={error()}>
                <p class="error" role="alert" data-testid="acct-error">{error()}</p>
              </Show>
              <div class="row">
                <button type="button" class="btn" onClick={() => props.onView('create')} data-testid="acct-to-create">
                  New here
                </button>
                <button type="submit" class="btn primary" disabled={busy()} data-testid="acct-submit">
                  {busy() ? 'Signing in…' : 'Sign in'}
                </button>
              </div>
            </form>
          </Match>

          <Match when={props.view === 'confirm'}>
            <div>
              <h2>Check your inbox</h2>
              <p class="hint">
                We sent a link to <b>{props.account.pendingEmail ?? 'your email'}</b>. Until you open it you can keep playing, but linking waits for the confirmed account.
              </p>
              <Show when={error()}>
                <p class="error" role="alert" data-testid="acct-error">{error()}</p>
              </Show>
              {/* Every way out of a mistyped or missing email, so this view is never a dead end. */}
              <p class="hint links">
                <button type="button" class="linkish" disabled={busy()} onClick={resend} data-testid="acct-resend">
                  Send it again
                </button>
                {' · '}
                <button
                  type="button"
                  class="linkish"
                  onClick={() => {
                    setEmail(props.account.pendingEmail ?? '');
                    props.onView('create');
                  }}
                  data-testid="acct-change-email"
                >
                  Wrong address? Change it
                </button>
                {' · '}
                <button type="button" class="linkish" onClick={() => props.onView('signin')} data-testid="acct-confirm-to-signin">
                  Sign in to another account
                </button>
              </p>
              <div class="row">
                <button type="button" class="btn" onClick={() => props.onClose()}>
                  Later
                </button>
                <button type="button" class="btn primary" disabled={busy()} onClick={confirmed} data-testid="acct-confirmed">
                  {busy() ? 'Checking…' : 'I opened it'}
                </button>
              </div>
            </div>
          </Match>

          <Match when={props.view === 'profile'}>
            <div>
              <h2>{props.account.handle ? `@${props.account.handle}` : 'Pick your handle'}</h2>
              <p class="hint">
                <Show when={props.account.email}>
                  {props.account.email} ·{' '}
                </Show>
                {props.account.cardCount === 1 ? '1 card linked' : `${props.account.cardCount} cards linked`}
              </p>
              {/* The handle is the name on everything linked; changing it is possible but not
                  something the profile invites. It sits behind one quiet link and a 30-day rule. */}
              <Show
                when={!props.account.handle || changingHandle()}
                fallback={
                  <p class="hint links">
                    <button type="button" class="linkish" onClick={() => setChangingHandle(true)} data-testid="acct-change-handle">
                      Change handle
                    </button>
                    <span style="color:var(--fg-faint)"> · once every 30 days</span>
                  </p>
                }
              >
                <form onSubmit={saveHandle}>
                  <label>
                    <span>{props.account.handle ? 'New handle' : 'Handle'}</span>
                    <div class="at">
                      <b>@</b>
                      <input ref={first} type="text" autocomplete="nickname" autocapitalize="off" spellcheck={false} placeholder={props.account.handle ?? 'yourname'} value={handle()} onInput={(e) => setHandle(e.currentTarget.value)} data-testid="acct-handle" />
                    </div>
                  </label>
                  <Show when={error()}>
                    <p class="error" role="alert" data-testid="acct-error">{error()}</p>
                  </Show>
                  <div class="row">
                    <Show when={props.account.handle}>
                      <button type="button" class="btn" onClick={() => { setChangingHandle(false); setHandle(''); setError(null); }}>
                        Keep it
                      </button>
                    </Show>
                    <button type="submit" class="btn primary" disabled={busy() || !handle().trim()} data-testid="acct-save-handle">
                      {props.account.handle ? 'Change' : 'Claim handle'}
                    </button>
                  </div>
                </form>
              </Show>
              <Show when={error() && props.account.handle && !changingHandle()}>
                <p class="error" role="alert" data-testid="acct-error">{error()}</p>
              </Show>
              <Show when={props.account.handle}>
                <MakerPanel onGoTo={props.onGoTo} onRemoved={props.onRemoved} toast={props.toast} />
              </Show>
              <Show when={props.account.isAdmin}>
                <StatsPanel />
              </Show>
              <div class="row" style="margin-top:14px">
                <button type="button" class="btn" onClick={signOut} disabled={busy()} data-testid="acct-signout">
                  Sign out
                </button>
              </div>
            </div>
          </Match>
        </Switch>
      </div>
    </Show>
  );
}
